import {
  countryCodeFromText,
  countryDisplayName,
  SHIPPING_ADDRESS_FIELD_MAX,
  SHIPPING_ADDRESS_FIELDS,
  type ShippingAddress,
  US_STATE_OPTIONS,
} from "@loot-os/api-contract";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";

import type { ShippingAddressDraft } from "../lib/shippingAddressDraft";

const WIDE_FIELDS: ReadonlySet<keyof ShippingAddress> = new Set(["addressLine1", "addressLine2"]);

const US_STATE_LABELS = Object.fromEntries(US_STATE_OPTIONS.map((s) => [s.code, s.name]));

/**
 * The address form's fields, phone included, in a two-column grid, and a US address picks its
 * state. Shared by the end user's address and an order's. The country is picked from `countries`
 * (ISO codes), or with `countryInput="text"` typed as a name or code with them as suggestions,
 * Retool's free-text field, which leaves the state alone when the country changes.
 */
export function ShippingAddressFields({
  idPrefix,
  draft,
  countries,
  countryInput = "select",
  onChange,
}: {
  idPrefix: string;
  draft: ShippingAddressDraft;
  countries: readonly string[];
  countryInput?: "select" | "text";
  onChange: (draft: ShippingAddressDraft) => void;
}) {
  const countryLabels = Object.fromEntries(
    countries.map((code) => [code, countryDisplayName(code)]),
  );
  const isUs =
    (countryInput === "text" ? countryCodeFromText(draft.country) : draft.country) === "US";
  return (
    <>
      {SHIPPING_ADDRESS_FIELDS.map((field) => (
        <div
          key={field.key}
          className={`grid gap-2 ${WIDE_FIELDS.has(field.key) ? "sm:col-span-2" : ""}`}
        >
          <Label htmlFor={`${idPrefix}-${field.key}`}>{field.label}</Label>
          {field.key === "country" && countryInput === "text" ? (
            <>
              <Input
                id={`${idPrefix}-country`}
                value={draft.country}
                maxLength={SHIPPING_ADDRESS_FIELD_MAX}
                required
                autoComplete="off"
                list={`${idPrefix}-countries`}
                onChange={(e) => onChange({ ...draft, country: e.target.value })}
              />
              <datalist id={`${idPrefix}-countries`}>
                {countries.map((code) => (
                  <option key={code} value={countryLabels[code]} />
                ))}
              </datalist>
            </>
          ) : field.key === "country" ? (
            <Select
              value={draft.country || null}
              items={countryLabels}
              onValueChange={(value) =>
                // A new country empties the region: a state code means nothing elsewhere.
                onChange({ ...draft, country: value ?? "", region: "" })
              }
            >
              <SelectTrigger id={`${idPrefix}-country`} className="w-full">
                <SelectValue placeholder="Choose a country" />
              </SelectTrigger>
              <SelectContent>
                {countries.map((code) => (
                  <SelectItem key={code} value={code}>
                    {countryLabels[code]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : field.key === "region" && isUs ? (
            <Select
              value={draft.region || null}
              items={US_STATE_LABELS}
              onValueChange={(value) => onChange({ ...draft, region: value ?? "" })}
            >
              <SelectTrigger id={`${idPrefix}-region`} className="w-full">
                <SelectValue placeholder="Choose a state" />
              </SelectTrigger>
              <SelectContent>
                {US_STATE_OPTIONS.map((s) => (
                  <SelectItem key={s.code} value={s.code}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              id={`${idPrefix}-${field.key}`}
              value={draft[field.key]}
              maxLength={SHIPPING_ADDRESS_FIELD_MAX}
              required={field.required}
              autoComplete="off"
              onChange={(e) => onChange({ ...draft, [field.key]: e.target.value })}
            />
          )}
        </div>
      ))}
    </>
  );
}
